import type { ReactNode } from 'react'
import { useIntl } from '@/intl'
import { getFooterColumnTitleTags } from '@/scopes/common/domain/policies/catalogue'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import Footer from './Footer'

export function NonCatalogueContent() {
  const intl = useIntl()
  const footerColumn1 = useConfiguration('footer.column1')
  const footerColumn2 = useConfiguration('footer.column2')
  const footerColumn3 = useConfiguration('footer.column3')
  const aboutColumn = useConfiguration('footer.aboutColumn') ?? []
  const privacyColumn = useConfiguration('footer.privacyColumn') ?? []
  const helpColumn = useConfiguration('footer.helpColumn') ?? []
  const aboutColumnTitle = useConfiguration('footer.aboutColumnTitle')
  const privacyColumnTitle = useConfiguration('footer.privacyColumnTitle')
  const helpColumnTitle = useConfiguration('footer.helpColumnTitle')
  const links = useConfiguration('links')
  const [
    footerColumn1TitleTag,
    footerColumn2TitleTag,
    footerColumn3TitleTag,
  ] = getFooterColumnTitleTags()

  const renderStaticColumn = (
    column: typeof aboutColumn,
    titleFromConfig: string | undefined,
    fallbackIntlKey: string,
    fallbackContent: ReactNode,
  ) => (
    <Footer.Block>
      <Footer.Header>
        {titleFromConfig ?? intl.formatMessage({ id: fallbackIntlKey })}
      </Footer.Header>
      <Footer.List>
        {column.length > 0
          ? column.map(({ text, link }) => (
              <Footer.ListItem key={text} href={link}>
                {text}
              </Footer.ListItem>
            ))
          : fallbackContent}
      </Footer.List>
    </Footer.Block>
  )

  const aboutFallback = (
    <>
      <Footer.ListItem href={links.about}>{intl.formatMessage({ id: 'footer_about' })}</Footer.ListItem>
      <Footer.ListItem href={links.responsibleTravel}>{intl.formatMessage({ id: 'footer_responsible_travel' })}</Footer.ListItem>
      <Footer.ListItem href={links.jobs}>{intl.formatMessage({ id: 'footer_jobs' })}</Footer.ListItem>
      <Footer.ListItem href={links.press}>{intl.formatMessage({ id: 'footer_press' })}</Footer.ListItem>
      <Footer.ListItem href={links.sitemap}>{intl.formatMessage({ id: 'footer_sitemap' })}</Footer.ListItem>
    </>
  )

  const privacyFallback = (
    <>
      <Footer.ListItem href={links.privacy}>{intl.formatMessage({ id: 'footer_privacy' })}</Footer.ListItem>
      <Footer.ListItem href={links.cookiesSettings}>{intl.formatMessage({ id: 'footer_cookies' })}</Footer.ListItem>
      <Footer.ListItem href={links.accessibility}>{intl.formatMessage({ id: 'footer_accessibility' })}</Footer.ListItem>
      <Footer.ListItem href={links.marketingPreferences}>{intl.formatMessage({ id: 'footer_marketing_preferences' })}</Footer.ListItem>
      {links.disclaimer && <Footer.ListItem href={links.disclaimer}>{intl.formatMessage({ id: 'footer_disclaimer' })}</Footer.ListItem>}
    </>
  )

  const helpFallback = (
    <>
      <Footer.ListItem href={links.support}>{intl.formatMessage({ id: 'footer_support' })}</Footer.ListItem>
      <Footer.ListItem href={links.login}>{intl.formatMessage({ id: 'my_portal' })}</Footer.ListItem>
      <Footer.ListItem href={links.conditions}>{intl.formatMessage({ id: 'footer_conditions' })}</Footer.ListItem>
      {links.partnersAndWorksCouncils && <Footer.ListItem href={links.partnersAndWorksCouncils}>{intl.formatMessage({ id: 'footer_partners_and_work_councils' })}</Footer.ListItem>}
      {links.holidayGuarantee && (
        <Footer.ListItem href={links.holidayGuarantee}>
          {intl.formatMessage({ id: 'footer_holiday_guarantee' })}
        </Footer.ListItem>
      )}
      {links.termsAndConditions && (
        <Footer.ListItem href={links.termsAndConditions}>
          {intl.formatMessage({ id: 'footer_terms_and_conditions' })}
        </Footer.ListItem>
      )}
      {links.travelInformation && <Footer.ListItem href={links.travelInformation}>{intl.formatMessage({ id: 'footer_travel_information' })}</Footer.ListItem>}
      <Footer.ListItem href={links.newsletter}>{intl.formatMessage({ id: 'footer_newsletter' })}</Footer.ListItem>
    </>
  )

  return (
    <>
      <Footer.Block>
        <Footer.Header>{intl.formatMessage({ id: footerColumn1TitleTag })}</Footer.Header>
        <Footer.List>
          {footerColumn1.map(({ text, link }) => (
            <Footer.ListItem key={text} href={link}>
              {text}
            </Footer.ListItem>
          ))}
        </Footer.List>
      </Footer.Block>
      <Footer.Block>
        <Footer.Header>{intl.formatMessage({ id: footerColumn2TitleTag })}</Footer.Header>
        <Footer.List>
          {footerColumn2.map(({ text, link }) => (
            <Footer.ListItem key={text} href={link}>
              {text}
            </Footer.ListItem>
          ))}
        </Footer.List>
      </Footer.Block>
      <Footer.Block>
        <Footer.Header>{intl.formatMessage({ id: footerColumn3TitleTag })}</Footer.Header>
        <Footer.List>
          {footerColumn3.map(({ text, link }) => (
            <Footer.ListItem key={text} href={link}>
              {text}
            </Footer.ListItem>
          ))}
        </Footer.List>
      </Footer.Block>
      {renderStaticColumn(aboutColumn, aboutColumnTitle, 'footer_about', aboutFallback)}
      {renderStaticColumn(privacyColumn, privacyColumnTitle, 'footer_privacy_cookies', privacyFallback)}
      {renderStaticColumn(helpColumn, helpColumnTitle, 'footer_need_help', helpFallback)}
    </>
  )
}
